import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

const API_URL = (process.env.REACT_APP_API_URL || "").trim().replace(/\/$/, "");
const MAX_POLLS = 40;

// Signzy return page (route /esign/:token). Signzy redirects the signer here
// with ?status=success|failed after Aadhaar OTP.
//
// Inside the e-sign overlay (iframe) on the Apply Loan page this page only
// tells the parent window what happened and the parent takes over. When the
// signer used the "open in a new tab" fallback, this page polls the backend
// (which pulls the contract from Signzy) and shows the outcome.
const ESignPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const status = String(searchParams.get("status") || "").toLowerCase();
  const inIframe = typeof window !== "undefined" && window.parent !== window;
  const relayToParent = inIframe && Boolean(status);

  const [state, setState] = useState({
    loading: true,
    completed: false,
    failed: false,
    error: "",
  });

  useEffect(() => {
    if (!relayToParent) return;
    try {
      window.parent.postMessage(
        { type: "esign-return", status, token },
        window.location.origin,
      );
    } catch (error) {
      console.error("eSign return: could not notify parent window", error);
    }
  }, [relayToParent, status, token]);

  useEffect(() => {
    if (relayToParent) return undefined;
    let cancelled = false;
    let timer = null;
    let polls = 0;
    const check = async () => {
      try {
        const query = `sync=1${status ? `&status=${encodeURIComponent(status)}` : ""}`;
        const res = await fetch(
          `${API_URL}/api/esign/return/${encodeURIComponent(token)}/status?${query}`,
        );
        const json = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !json.success) {
          setState({
            loading: false,
            completed: false,
            failed: false,
            error: json.message || "Invalid or expired eSign link.",
          });
          return;
        }
        setState({
          loading: false,
          completed: json.completed === true,
          failed: json.failed === true,
          error: "",
        });
        // Signed at Signzy but the webhook has not landed yet: keep asking.
        if (!json.completed && status === "success" && polls < MAX_POLLS) {
          polls += 1;
          timer = setTimeout(check, 3000);
        }
      } catch (error) {
        if (!cancelled) {
          setState({
            loading: false,
            completed: false,
            failed: false,
            error: "Failed to connect to the server.",
          });
        }
      }
    };
    check();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [relayToParent, status, token]);

  const page = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    background: "#f8fafc",
    padding: "20px",
    fontFamily: "inherit",
  };
  const card = {
    background: "#fff",
    padding: "40px",
    borderRadius: "16px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    textAlign: "center",
    maxWidth: "520px",
    width: "100%",
  };
  const button = {
    display: "inline-block",
    marginTop: "8px",
    background: "#1d4ed8",
    color: "#fff",
    padding: "12px 28px",
    borderRadius: "10px",
    fontWeight: 700,
    textDecoration: "none",
  };

  if (relayToParent) {
    return (
      <div style={page}>
        <p style={{ color: "#64748b", fontSize: "16px" }}>
          Returning to your application…
        </p>
      </div>
    );
  }

  let icon = "⏳";
  let title = "Finalizing your e-sign…";
  let body =
    "Your signature was received. We are fetching the signed sanction letter from the e-sign provider. This usually takes a few seconds.";
  if (state.loading) {
    icon = "🔒";
    title = "Checking your e-sign…";
    body = "Please wait a moment.";
  } else if (state.error) {
    icon = "⚠️";
    title = "Link not valid";
    body = state.error;
  } else if (state.completed) {
    icon = "✅";
    title = "Sanction letter e-signed";
    body =
      "Your Aadhaar e-signature has been recorded and the signed sanction letter is saved with your application. You can close this tab and return to the application.";
  } else if (status === "failed" || state.failed) {
    icon = "❌";
    title = "E-sign not completed";
    body =
      "The Aadhaar e-sign was not completed. Go back to your application and start the signing again.";
  } else if (!status) {
    icon = "✍️";
    title = "Aadhaar e-sign";
    body =
      "This page confirms your e-sign once you finish the Aadhaar OTP. Please sign from your loan application.";
  }

  return (
    <div style={page}>
      <div style={card} role="status" aria-live="polite">
        <div style={{ fontSize: "44px", marginBottom: "12px" }} aria-hidden="true">
          {icon}
        </div>
        <h1 style={{ margin: "0 0 12px", fontSize: "22px", color: "#0f172a" }}>{title}</h1>
        <p style={{ color: "#475569", lineHeight: 1.55, margin: "0 0 20px" }}>{body}</p>
        <Link to="/dashboard/apply-loan" style={button}>
          Back to my application
        </Link>
      </div>
    </div>
  );
};

export default ESignPage;
